import React, { useEffect, useState } from "react";
import { Copy, Loader2, Mail, X } from "lucide-react";
import { toast } from "sonner";
import { draftEmail } from "../lib/api";

const LANGS = ["English", "Portuguese", "Japanese", "German", "Hindi"];

export default function DraftEmailModal({ lead, agent, onClose }) {
  const [language, setLanguage] = useState(agent.language || "English");
  const [loading, setLoading] = useState(true);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  // cache drafts per language so switching doesn't re-hit Claude
  const [cache, setCache] = useState({});

  const generate = async (lang, force = false) => {
    if (!force && cache[lang]) {
      setSubject(cache[lang].subject);
      setBody(cache[lang].body);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await draftEmail(lead.id, lang);
      const entry = { subject: res.subject || "", body: res.body || "" };
      setSubject(entry.subject);
      setBody(entry.body);
      setCache((c) => ({ ...c, [lang]: entry }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Draft failed");
      setSubject("");
      setBody("");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    generate(language);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const copyAll = async () => {
    const text = `Subject: ${subject}\n\n${body}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Email copied to clipboard");
    } catch {
      toast.error("Clipboard blocked — copy manually");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-aot-bg/80 backdrop-blur-sm flex items-center justify-center p-4 aot-fade-in"
      onClick={onClose}
      data-testid="draft-email-modal"
    >
      <div
        className="w-full max-w-2xl bg-aot-surface border border-aot-border max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-aot-border">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Mail className="w-3.5 h-3.5 text-aot-teal" />
              <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-aot-muted">
                Claude · personalised draft
              </span>
            </div>
            <h3 className="font-display text-xl font-semibold tracking-tight text-aot-text">
              To: {lead.name}
            </h3>
            <p className="text-xs font-mono text-aot-muted mt-1">
              {lead.email} · {lead.org || "—"}
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="draft-email-close"
            className="text-aot-muted hover:text-aot-text p-1"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Language switch */}
        <div className="px-6 py-3 border-b border-aot-border flex items-center gap-1 flex-wrap">
          <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-aot-subtle mr-2">
            Language
          </span>
          {LANGS.map((lang) => (
            <button
              key={lang}
              disabled={loading}
              onClick={() => {
                setLanguage(lang);
                generate(lang);
              }}
              data-testid={`draft-lang-${lang.toLowerCase()}`}
              className={[
                "px-3 py-1 text-xs font-mono uppercase tracking-wider border transition-colors",
                language === lang
                  ? "border-aot-teal text-aot-teal bg-aot-teal/10"
                  : "border-aot-border text-aot-muted hover:text-aot-text hover:border-aot-subtle",
                loading ? "opacity-50 cursor-not-allowed" : "",
              ].join(" ")}
            >
              {lang}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-aot-muted">
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              <span className="text-xs font-mono uppercase tracking-wider">
                Claude is drafting…
              </span>
            </div>
          ) : (
            <>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-[0.25em] text-aot-muted mb-2">
                  Subject
                </label>
                <input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  data-testid="draft-subject-input"
                  className="w-full bg-aot-bg border border-aot-border px-3 py-2 text-sm text-aot-text focus:outline-none focus:border-aot-teal"
                />
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-[0.25em] text-aot-muted mb-2">
                  Body
                </label>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={12}
                  data-testid="draft-body-textarea"
                  className="w-full bg-aot-bg border border-aot-border px-3 py-2 text-sm text-aot-text leading-relaxed focus:outline-none focus:border-aot-teal resize-y font-sans"
                />
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-aot-border">
          <span className="text-[10px] font-mono uppercase tracking-wider text-aot-subtle">
            Draft only · not sent
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-xs font-mono uppercase tracking-wider text-aot-muted hover:text-aot-text transition-colors"
            >
              Close
            </button>
            <button
              onClick={copyAll}
              disabled={loading || !body}
              data-testid="draft-copy-button"
              className="inline-flex items-center gap-2 px-4 py-2 bg-aot-teal text-aot-bg text-xs font-mono uppercase tracking-wider hover:opacity-90 transition-opacity disabled:opacity-40"
            >
              <Copy className="w-3.5 h-3.5" />
              Copy email
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
